import {
    Box,
    Chip,
    Divider,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { FC, memo, useState } from 'react';
import { useEditLab } from 'context/edit-lab-context';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import PriceDifferenceDialog from './price-difference-dialog.component';
import { useTranslation } from 'react-i18next';
import { GroupMaterials, ExtendedMaterialGroup } from 'models/organization';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'text', width: 80 },
];

const MaterialGroups: FC = memo(() => {
    const { t: translate } = useTranslation(['common', 'organization', 'orders']);
    const { materialGroups, isMaterialGroupsLoading } = useEditLab();
    const [selected, setSelected] = useState<{ material: GroupMaterials; group: ExtendedMaterialGroup } | null>(null);

    const openDialog = (material: GroupMaterials, group: ExtendedMaterialGroup) => setSelected({ material, group });
    const closeDialog = () => setSelected(null);

    return (
        <>
            <Divider sx={{ mt: 3, mb: 3 }} />

            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {translate('organization:MATERIAL_GROUPS')}
                </Typography>

                <TableContainer component={Paper} sx={{ maxHeight: 500, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                    {translate('organization:MATERIAL_GROUP_NAME')}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                    {translate('organization:MATERIAL_NAME')}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                    {translate('organization:AVAILABLE')}
                                </TableCell>
                                <TableCell sx={{ backgroundColor: 'background.paper' }}>
                                    {translate('organization:PRICE_DIFFERENCE')}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {isMaterialGroupsLoading && (
                                <SkeletonTable columns={skeletonColumns} rows={10} renderRowsOnly />
                            )}

                            {materialGroups?.map((group) => {
                                const groupDiffMaterials = group.materialPriceDifferenceForGroup?.[0]?.materials || [];

                                const priceMap = groupDiffMaterials.reduce((acc: Record<string, number>, x) => {
                                    if (x.priceDiff !== null) acc[x.materialName] = x.priceDiff;
                                    return acc;
                                }, {});

                                const availabilityMap = groupDiffMaterials.reduce((acc: Record<string, boolean>, x) => {
                                    acc[x.materialName] = x.isAvailable !== false;
                                    return acc;
                                }, {});

                                return (
                                    <TableRow key={group._id}>
                                        <TableCell>{group.name}</TableCell>

                                        <TableCell>
                                            <Box>
                                                {group.materials.map((m) => (
                                                    <Typography key={m._id} variant="body2" sx={{ mb: 0.5 }}>
                                                        {m.material}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </TableCell>

                                        <TableCell>
                                            <Box>
                                                {group.materials.map((m) => {
                                                    const isAvailable = availabilityMap[m.material];
                                                    return (
                                                        <Box key={m._id} sx={{ mb: 0.5 }}>
                                                            {isAvailable !== undefined && (
                                                                <Chip
                                                                    label={
                                                                        isAvailable
                                                                            ? translate('organization:AVAILABLE')
                                                                            : translate('organization:UNAVAILABLE')
                                                                    }
                                                                    color={isAvailable ? 'success' : 'error'}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </TableCell>

                                        <TableCell
                                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                            onClick={() => openDialog(group.materials[0], group)}
                                        >
                                            <Box>
                                                {group.materials.map((m) => {
                                                    const price = priceMap[m.material];
                                                    const isNegative = price && price < 0;
                                                    const displayValue = price ? Math.abs(price) : '\u00A0';

                                                    return (
                                                        <Typography
                                                            key={m._id}
                                                            variant="body2"
                                                            sx={{
                                                                mb: 0.5,
                                                                fontFamily: 'monospace',
                                                            }}
                                                        >
                                                            {price ? (
                                                                <>
                                                                    {isNegative ? '-' : '\u00A0'}
                                                                    {displayValue}
                                                                </>
                                                            ) : (
                                                                '\u00A0'
                                                            )}
                                                        </Typography>
                                                    );
                                                })}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {selected && (
                <PriceDifferenceDialog
                    open={true}
                    onClose={closeDialog}
                    material={selected.material}
                    group={selected.group}
                />
            )}
        </>
    );
});

MaterialGroups.displayName = 'MaterialGroups';

export default MaterialGroups;
